import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Clock, Database, Play } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import AdminNav from "@/components/AdminNav";
import BulkBar, { PurgeDialog, RowCheckbox, useSelection } from "@/components/BulkBar";
import { useDeployGuard } from "@/components/DeployGuard";
import { QuotaTimeline } from "@/components/QuotaTimeline";

function Countdown({ to }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);
  if (!to) return <span className="text-slate-600">—</span>;
  const diff = new Date(to).getTime() - Date.now();
  if (diff <= 0) return <span className="text-[#00FF87]">now</span>;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return (
    <span className="text-[#00F0FF]" data-testid="next-run-countdown">
      {h > 0 ? `${h}h ${m}m` : `${m}m`}
    </span>
  );
}

const STATUS_CLS = {
  success: "text-emerald-400", running: "text-sky-400", failed: "text-red-400",
  quota_exhausted: "text-amber-400", stopped: "text-violet-400",
  skipped_quota: "text-amber-400", interrupted: "text-slate-400", rate_limited: "text-orange-400",
};

function CachesPanel({ t }) {
  const [data, setData] = useState(null);
  const csel = useSelection();
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/caches");
      setData(r.data);
    } catch { /* panel stays hidden on error */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bulkDelete = async () => {
    try {
      const r = await api.post("/admin/caches/delete", { keys: [...csel.sel] });
      toast.success(`${t("admin.action_ok")}: ${r.data.deleted}`);
      csel.clear();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const purge = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/caches/purge", { confirm: "DELETE" });
      toast.success(`${t("admin.purge")}: ${r.data.purged} · ${t("admin.cache_immutable")}: ${r.data.kept_immutable}`);
      setPurgeOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const rows = data.caches;
  return (
    <div className="mb-6 rounded-xl p-4 hud-card" data-testid="caches-panel">
      <h2 className="mb-1 font-mono2 text-xs font-bold uppercase tracking-widest text-[#00F0FF]">{t("admin.caches_title")} ({data.total})</h2>
      <p className="mb-3 font-mono2 text-[9px] text-slate-500">{t("admin.cache_purge_hint_short")}</p>
      <BulkBar
        count={csel.count}
        onClear={csel.clear}
        t={t}
        actions={[{ key: "delete", label: t("admin.delete_user"), cls: "border-red-500/50 text-red-400 hover:bg-red-500/10", onClick: bulkDelete }]}
        onPurge={() => setPurgeOpen(true)}
      />
      <div className="max-h-72 overflow-y-auto overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-800 font-mono2 text-[9px] uppercase tracking-widest text-slate-500">
              <th className="w-6 py-1.5 pr-2" />
              <th className="py-1.5 pr-2">key</th>
              <th className="py-1.5 pr-2">endpoint</th>
              <th className="py-1.5 pr-2">params</th>
              <th className="py-1.5 pr-2">{t("admin.status")}</th>
              <th className="py-1.5">expires</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.key} className="border-b border-slate-800/50" data-testid="cache-row">
                <td className="py-1.5 pr-2"><RowCheckbox selected={csel.sel.has(c.key)} onToggle={() => csel.toggle(c.key)} testid={`cache-select-${c.key.slice(0, 8)}`} /></td>
                <td className="py-1.5 pr-2 font-mono2 text-[10px] text-slate-400" title={c.key}>{c.key.slice(0, 12)}…</td>
                <td className="py-1.5 pr-2 font-mono2 text-[10px] text-slate-300">{c.endpoint}</td>
                <td className="max-w-40 truncate py-1.5 pr-2 font-mono2 text-[10px] text-slate-500" title={c.params}>{c.params || "—"}</td>
                <td className="py-1.5 pr-2">
                  {c.immutable && <span className="rounded border border-[#00F0FF]/40 px-1 font-mono2 text-[9px] text-[#00F0FF]">{t("admin.cache_immutable")}</span>}
                  {c.expired && <span className="ml-1 rounded border border-slate-600 px-1 font-mono2 text-[9px] text-slate-500">{t("admin.cache_expired")}</span>}
                </td>
                <td className="whitespace-nowrap py-1.5 font-mono2 text-[10px] text-slate-500">{c.expires_at ? new Date(c.expires_at * 1000).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="py-3 text-center text-xs text-slate-600">{t("admin.caches_empty")}</p>}
      </div>
      <PurgeDialog open={purgeOpen} title={t("admin.purge_caches_title")} hint={t("admin.purge_caches_hint")} busy={busy} onClose={() => setPurgeOpen(false)} onConfirm={purge} t={t} />
    </div>
  );
}

function RetentionPanel({ t }) {
  const [ret, setRet] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const load = useCallback(async () => {
    try {
      const [r, e] = await Promise.all([api.get("/admin/retention"), api.get("/admin/retention/exports")]);
      setRet(r.data);
      setReceipts(e.data.exports || []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggleHold = async () => {
    try {
      await api.post("/admin/retention/hold", { hold: !ret.hold });
      toast.success(t("admin.retention_hold_saved"));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (!ret) return null;
  return (
    <div className="mb-6 rounded-xl p-4 hud-card" data-testid="retention-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-mono2 text-xs font-bold uppercase tracking-widest text-[#00F0FF]">
          <Database className="h-3.5 w-3.5" /> {t("admin.retention_title")}
        </h2>
        <button data-testid="retention-hold-toggle" onClick={toggleHold}
          className={`rounded-md border px-2 py-1 font-mono2 text-[9px] font-bold uppercase tracking-wider ${ret.hold ? "border-amber-500/50 text-amber-400" : "border-[#00FF87]/50 text-[#00FF87]"}`}>
          {ret.hold ? t("admin.retention_hold_on") : t("admin.retention_hold_off")}
        </button>
      </div>
      <p className="mb-3 font-mono2 text-[9px] text-slate-500">{t("admin.retention_hint")}</p>
      <div className="space-y-1 font-mono2 text-[10px]">
        {ret.collections.map((c) => (
          <div key={c.collection} className="flex justify-between border-b border-slate-800/60 pb-1" data-testid={`retention-row-${c.collection}`}>
            <span className="text-slate-300">{c.collection} <span className="text-slate-600">({c.window_days}d)</span></span>
            <span className="text-slate-400">{c.older_than_window} / {c.total}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 font-mono2 text-[9px] text-slate-600" data-testid="retention-classb">{t("admin.retention_classb")}: {ret.class_b.join(", ")}</p>
      <div className="mt-3" data-testid="retention-exports">
        <p className="mb-1 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{t("admin.retention_exports")}</p>
        {receipts.length === 0 && <p className="font-mono2 text-[9px] text-slate-600">{t("admin.retention_none")}</p>}
        {receipts.slice(0, 5).map((r, i) => (
          <p key={i} className="font-mono2 text-[9px] text-slate-400">{r.at?.slice(0, 19)} · {r.collection} · {r.purged} → {r.receipt?.backend}</p>
        ))}
      </div>
    </div>
  );
}


export default function AdminScheduler() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [alertCfg, setAlertCfg] = useState(null);
  const guard = useDeployGuard();
  const providerBusy = !!guard?.provider_job_running;

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([api.get("/admin/scheduler"), api.get("/admin/alerts/settings")]);
      setData(r.data);
      setAlertCfg(a.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const saveAlertCfg = async () => {
    try {
      await api.put("/admin/alerts/settings", { delta_threshold: Number(alertCfg.delta_threshold) });
      toast.success(t("admin.alerts_saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runNow = async (job) => {
    try {
      await api.post("/admin/jobs/run", { job, league_id: null });
      toast.success(t("admin.queued"));
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) return <p className="pt-16 text-center font-mono2 text-sm text-slate-500">{t("common.loading")}</p>;

  return (
    <div data-testid="admin-scheduler-page">
      <h1 className="mb-4 font-display text-3xl font-black uppercase tracking-tight text-slate-50 sm:text-4xl">{t("admin.scheduler_title")}</h1>
      <AdminNav />

      {data.unexpected_invocations?.length > 0 && (
        <div className="mb-4 rounded-xl border border-red-500/40 bg-red-500/5 p-3" data-testid="unexpected-invocations">
          <p className="font-mono2 text-[10px] font-bold uppercase tracking-wider text-red-400">{t("admin.unexpected_invocations")}</p>
          <ul className="mt-1 font-mono2 text-[10px] text-slate-300">
            {data.unexpected_invocations.map((u) => (
              <li key={`${u.name}-${u.at}`}>{u.at.slice(5, 16).replace("T", " ")} UTC · {u.name}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-6 grid gap-4 md:grid-cols-2" data-testid="scheduler-jobs">        {data.jobs.map((j) => (
          <div key={j.name} className="rounded-xl p-4 hud-card" data-testid={`cron-card-${j.name}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono2 text-xs font-bold uppercase tracking-wider text-slate-200">{j.name}</span>
              <span className={`font-mono2 text-[9px] uppercase ${j.enabled ? "text-emerald-400" : "text-slate-600"}`}>{j.enabled ? "ON" : "OFF"}</span>
            </div>
            <p className="mb-3 text-[11px] text-slate-500">{j.description}</p>
            <div className="mb-3 space-y-1 font-mono2 text-[10px]">
              <div className="flex justify-between"><span className="text-slate-500">{t("admin.cron_schedule")}</span><span className="text-slate-300">{j.cron_human} <span className="text-slate-600">({j.cron})</span></span></div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t("admin.last_invocation")}</span>
                {j.last_invocation ? (
                  <span className="text-right text-slate-300" data-testid={`cron-invocation-${j.name}`}>
                    {j.last_invocation.slice(5, 16).replace("T", " ")} UTC
                  </span>
                ) : <span className="text-red-400" data-testid={`cron-invocation-${j.name}`}>{t("admin.never_invoked")}</span>}
              </div>
              {j.enabled && (
                <div className="flex justify-between">
                  <span className="text-slate-500">{t("admin.previous_expected")}</span>
                  <span className="text-right" data-testid={`cron-expected-${j.name}`}>
                    <span className="text-slate-400">{j.previous_expected?.slice(5, 16).replace("T", " ")} UTC</span>
                    {j.missed_slot && <span className="ml-1 font-bold text-red-400" data-testid={`cron-missed-${j.name}`}>{t("admin.missed_slot")}</span>}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">{t("admin.last_job_run")}</span>
                {j.last_run ? (
                  <span className="text-right">
                    <span className={STATUS_CLS[j.last_run.status] || "text-slate-400"}>{j.last_run.status}</span>
                    <span className="text-slate-500"> · {j.last_run.started_at?.slice(11, 16)} UTC · {j.last_run.duration_s ?? "?"}s · {j.last_run.records} rec</span>
                  </span>
                ) : <span className="text-slate-600">—</span>}
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t("admin.next_run")}</span>
                {/* Schedules are UTC (the scheduler runs in UTC): show UTC first, local second,
                    both labelled — an unlabelled local time reads as a 2h drift in summer. */}
                <span>{j.next_run ? <><Countdown to={j.next_run} /> <span className="text-slate-600">({j.next_run.slice(11, 16)} UTC · {new Date(j.next_run).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} {t("admin.local_time")})</span></> : "—"}</span>
              </div>
              {j.last_run?.detail && j.last_run.status !== "success" && (
                <p className="truncate text-[9px] text-red-400/80" title={j.last_run.detail}>{j.last_run.detail}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {j.jobs.map((job) => (
                <button key={job} data-testid={`run-now-${job}`} onClick={() => runNow(job)} disabled={providerBusy} title={providerBusy ? t("admin.deploy_guard_busy") : undefined} className="flex items-center gap-1 rounded-md border border-[#1F293D] px-2 py-1 font-mono2 text-[9px] font-bold uppercase tracking-wider text-slate-300 hover:border-[#00FF87]/50 hover:text-[#00FF87] disabled:cursor-not-allowed disabled:opacity-40">
                  <Play className="h-2.5 w-2.5" /> {t(`admin.${job}`)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <QuotaTimeline />

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl p-4 hud-card" data-testid="quota-panel">
          <h2 className="mb-1 flex items-center gap-2 font-mono2 text-xs font-bold uppercase tracking-widest text-[#FFD600]">
            <Activity className="h-3.5 w-3.5" /> {t("admin.quota_title")}
          </h2>
          <p className="mb-3 flex items-center gap-1 font-mono2 text-[9px] text-amber-400/90" data-testid="quota-reset-note"><Clock className="h-3 w-3" /> {t("admin.quota_reset", { reset: data.quota.reset })} ({data.quota.next_reset})</p>
          {data.quota.provider?.daily_remaining != null && (
            <p className="mb-2 font-mono2 text-[10px] text-slate-400" data-testid="quota-provider">
              {t("admin.quota_provider", {
                remaining: data.quota.provider.daily_remaining,
                limit: data.quota.provider.daily_limit ?? "?",
                at: (data.quota.provider.synced_at || "").slice(5, 16).replace("T", " "),
              })}
              {data.quota.provider.disagrees && (
                <span className="ml-1 font-bold text-amber-400" data-testid="quota-disagrees">{t("admin.quota_disagrees")}</span>
              )}
            </p>
          )}
          {data.quota.reserve?.enforced && (
            <p className={`mb-2 font-mono2 text-[10px] font-bold ${data.quota.reserve.available === 0 ? "text-red-400" : data.quota.reserve.available < 500 ? "text-amber-400" : "text-[#00FF87]"}`} data-testid="quota-reserve">
              {t("admin.quota_reserve", { available: data.quota.reserve.available, reserve: data.quota.reserve.requests })}
            </p>
          )}
          {data.quota.sources.map((s) => (
            <div key={s.source} className="mb-2 border-b border-slate-800/60 pb-2 font-mono2 text-[10px]" data-testid={`quota-${s.source}`}>
              <div className="flex justify-between text-slate-300">
                <span>{s.source}</span>
                <span>{s.used}{s.limit ? ` / ${s.limit}` : ""} {s.remaining !== null && s.remaining !== undefined ? `· ${s.remaining} left` : `· ${s.limit_note}`}</span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>{t("admin.hits")}</span>
                <span>{s.hits} · {t("admin.hit_rate")} {s.hit_rate !== null ? `${s.hit_rate}%` : "—"}</span>
              </div>
              {s.rpm_limit && (
                <div className="flex justify-between text-slate-500" data-testid={`rpm-${s.source}`}>
                  <span>{t("admin.rpm_peak")}</span>
                  <span className={s.peak_per_minute >= s.rpm_limit ? "text-orange-400" : "text-slate-400"}>
                    {s.peak_per_minute} {t("admin.rpm_of", { limit: s.rpm_limit })}
                    {s.provider_rpm_limit ? ` · ${t("admin.rpm_provider", { limit: s.provider_rpm_limit })}` : ""}
                  </span>
                </div>
              )}
            </div>
          ))}
          {data.quota.guard && (
            <div className="mt-3 border-t border-slate-800/60 pt-2" data-testid="quota-guard">
              <p className="mb-1 font-mono2 text-[9px] uppercase tracking-wider text-[#FFD600]">{t("admin.quota_guard_title")}</p>
              {(() => {
                const g = data.quota.guard;
                const used = data.quota.sources.find((s) => s.source === "API-Football")?.used ?? 0;
                const pct = (v) => Math.min(100, (v / g.plan_daily_limit) * 100);
                return (
                  <div>
                    <div className="relative h-2 rounded bg-slate-800">
                      <div className={`absolute inset-y-0 left-0 rounded ${used >= g.daily_budget ? "bg-red-500" : used >= 0.8 * g.daily_budget ? "bg-amber-400" : "bg-[#00FF87]"}`} style={{ width: `${pct(used)}%` }} data-testid="quota-guard-used-bar" />
                      <div className="absolute -inset-y-0.5 w-px bg-[#FFD600]" style={{ left: `${pct(g.daily_budget)}%` }} data-testid="quota-guard-marker" />
                    </div>
                    <p className="mt-1 font-mono2 text-[9px] text-slate-400" data-testid="quota-guard-daily">
                      {t("admin.quota_guard_daily")}: {used} / {g.daily_budget} / {g.plan_daily_limit}
                    </p>
                    <p className="mt-1 flex justify-between font-mono2 text-[9px] text-slate-400" data-testid="quota-guard-rpm">
                      <span>{t("admin.quota_guard_rpm")}: {g.rpm_throttle} / {g.plan_rpm_limit}</span>
                      <span className={g.rpm_throttle >= g.plan_rpm_limit ? "text-red-400" : g.rpm_throttle >= 0.8 * g.plan_rpm_limit ? "text-amber-400" : "text-[#00FF87]"}>{g.rpm_throttle >= g.plan_rpm_limit ? "!" : g.rpm_throttle >= 0.8 * g.plan_rpm_limit ? "80%+" : "OK"}</span>
                    </p>
                    <p className="mt-1 font-mono2 text-[8px] leading-relaxed text-slate-600">{t("admin.quota_guard_hint")}</p>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        <div className="rounded-xl p-4 hud-card" data-testid="backlog-panel">
          <h2 className="mb-3 flex items-center gap-2 font-mono2 text-xs font-bold uppercase tracking-widest text-[#D946EF]">
            <Database className="h-3.5 w-3.5" /> {t("admin.backlog_title")}
          </h2>
          <div className="space-y-2 font-mono2 text-[10px]">
            <div className="flex justify-between"><span className="text-slate-500">{t("admin.fixtures_pending")}</span><span className="text-slate-200">{data.backlog.fixtures_pending_extraction}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("admin.oldest_pending")}</span><span className="text-slate-200">{data.backlog.oldest_pending_date || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("admin.sets_pending")}</span><span className="text-slate-200">{data.backlog.card_sets_pending}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("admin.queue_pending")}</span><span className="text-slate-200">{data.backlog.cards_unlinked}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("admin.immutable_cache")}</span><span className="text-[#00FF87]" data-testid="immutable-cache-count">{data.immutable_cache_entries ?? "—"}</span></div>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl p-4 hud-card" data-testid="alerts-settings-panel">
        <h2 className="mb-1 flex items-center gap-2 font-mono2 text-xs font-bold uppercase tracking-widest text-[#00FF87]">
          <Activity className="h-3.5 w-3.5" /> {t("admin.alerts_title")}
        </h2>
        <p className="mb-3 font-mono2 text-[9px] text-slate-500">{t("admin.rise_hint")}</p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="font-mono2 text-[10px] uppercase tracking-wider text-slate-400">{t("admin.rise_threshold")}</label>
          <input
            type="number" min="1" max="100"
            data-testid="rise-threshold-input"
            className="w-24 rounded-md border border-[#1F293D] bg-[#0B0E14] px-2.5 py-1.5 text-xs text-slate-200 focus:border-[#00FF87] focus:outline-none"
            value={alertCfg?.delta_threshold ?? 10}
            onChange={(e) => setAlertCfg({ ...alertCfg, delta_threshold: e.target.value })}
          />
          <button data-testid="rise-threshold-save" onClick={saveAlertCfg} className="btn-volt">{t("admin.threshold_save")}</button>
        </div>
      </div>

      <CachesPanel t={t} />

      {data.qualification && (
        <div className="mb-6 rounded-xl p-4 hud-card" data-testid="qualification-panel">
          <h2 className="mb-3 flex items-center gap-2 font-mono2 text-xs font-bold uppercase tracking-widest text-[#00F0FF]">
            <Activity className="h-3.5 w-3.5" /> {t("admin.qual_title")}
          </h2>
          <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="font-display text-2xl font-black text-[#FFD600]" data-testid="qual-done">{data.qualification.done}</span>
            <span className="font-mono2 text-xs text-slate-500">/ {data.qualification.total} {t("admin.qual_done")}</span>
            <span className="ml-auto font-mono2 text-[10px] text-[#00FF87]" data-testid="qual-public">{data.qualification.public} {t("admin.qual_public")}</span>
          </div>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-[#111827]">
            <div className="h-full rounded-full bg-gradient-to-r from-[#00FF87] to-[#FFD600] transition-all duration-700"
              style={{ width: `${data.qualification.percent}%` }} data-testid="qual-bar" />
          </div>
          <p className="font-mono2 text-[10px] text-slate-400" data-testid="qual-cycles">
            {data.qualification.remaining} {t("admin.qual_remaining")} ·{" "}
            {data.qualification.cycles_remaining
              ? t("admin.qual_cycles", {
                  n: data.qualification.cycles_remaining,
                  rate: data.qualification.requests_per_player,
                  at: data.qualification.next_cycle_at.slice(11, 16) + " UTC",
                })
              : t("admin.qual_cycles_done")}
          </p>
          <p className="mt-1 font-mono2 text-[9px] text-slate-600">
            {data.qualification.requests_per_player_measured ? t("admin.qual_estimate") : t("admin.qual_default_rate")}
          </p>
        </div>
      )}

      <RetentionPanel t={t} />

      <div className="mb-6 rounded-xl p-4 hud-card" data-testid="data-facts-section">
        <h2 className="mb-3 flex items-center gap-2 font-mono2 text-xs font-bold uppercase tracking-widest text-[#00F0FF]">
          <Activity className="h-3.5 w-3.5" /> {t("admin.facts_title")}
        </h2>
        <div className="space-y-2">
          {(data.data_facts || []).map((f) => (
            <div key={f.key} className="rounded-lg border border-[#1E2638] bg-[#07090E]/60 px-3 py-2 font-mono2 text-[10px]" data-testid={`fact-${f.key}`}>
              <div className="flex justify-between text-slate-300">
                <span>{f.key}</span>
                <span className={["active", "senior_only", "exposed", "uncapped", "managed"].includes(f.value) ? "text-[#00FF87]" : f.value === "suspended" ? "text-red-400" : "text-amber-400"}>{f.value} · {f.verified_at}</span>
              </div>
              <p className="mt-1 text-slate-500">{f.note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
